import {
  FC,
  Fragment,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ActionIcon,
  Box,
  Flex,
  Portal,
  ScrollArea,
  Text,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconSend, IconX } from "@tabler/icons-react";
import Markdown from "react-markdown";

import useAuth from "@/hooks/useAuth";
import {
  CONVERSATION_GONE_CODES,
  JavisAiError,
  JavisAiEvent,
  createConversation,
  getAiBaseUrl,
  isAiConfigured,
  openEventStream,
  sendMessage,
  streamGreeting,
} from "@/services/javisAiChat";
import BotAvatar from "./BotAvatar";
import {
  ChatMessage,
  clearSession,
  expiresAtOf,
  loadSession,
  saveSession,
} from "./chatSessionStore";
import { useTranslation } from "react-i18next";
import i18n from "i18n/i18n";
import "./JavisAiChat.scss";

/**
 * Câu chào dự phòng khi `/greet` không trả gì (assistant không cấu hình lời chào, hoặc lỗi mạng).
 * Có câu chào sẵn thì khung chat không mở ra trống trơn.
 */
const getGreetingFallback = () => (i18n.t("AI_CHAT_GREETING"));

/** Thông báo cho người dùng theo mã lỗi của JAVIS AI (tài liệu tích hợp §7). */
const getErrorText = (): Record<string, string> => ({
  // Nhắc luôn chuyện restart: `env-cmd -f .env` chỉ đọc .env lúc khởi động tiến trình, hot-reload
  // KHÔNG nạp lại biến môi trường — sửa .env mà không tắt dev server thì vẫn thấy thông báo này.
  MISSING_AI_CHAT_HOST:
    i18n.t("AI_CHAT_HOST_NOT_CONFIGURED") +
    i18n.t("RESTART_DEV_SERVER_AFTER_ENV_CHANGE"),
  MISSING_AI_ASSISTANT_ID:
    i18n.t("AI_ASSISTANT_ID_NOT_CONFIGURED") +
    i18n.t("RESTART_DEV_SERVER_AFTER_ENV_CHANGE"),
  "ERR-APIKEY-MISSING": i18n.t("AI_REQUEST_MISSING_TOKEN"),
  "ERR-APIKEY-INVALID": i18n.t("AI_TOKEN_INVALID"),
  "ERR-APIKEY-INVALID-FORMAT": i18n.t("AI_TOKEN_MALFORMED"),
  "ERR-APIKEY-REVOKED": i18n.t("AI_TOKEN_REVOKED"),
  "ERR-APIKEY-EXPIRED": i18n.t("AI_TOKEN_EXPIRED"),
  "ERR-ASSISTANT-NOTFOUND": i18n.t("AI_ASSISTANT_NOT_FOUND"),
  "ERR-ASSISTANT-NOTPUBLISHED": i18n.t("AI_ASSISTANT_NOT_PUBLISHED"),
  "ERR-MEMORY-CUSTOMERID-REQUIRED":
    i18n.t("AI_MISSING_CUSTOMER_ID"),
});
const getErrorFallback = () => (i18n.t("AI_CONNECTION_FAILED"));
/** Hội thoại phía JAVIS AI hết hạn (hiện là 1 giờ) — bắt đầu mạch mới, AI không còn nhớ phần trước. */
const getExpiredNotice = () => (i18n.t("CHAT_SESSION_EXPIRED_RESTART"));
/** Luồng đóng mà không có chữ nào — khác lỗi kết nối, nên nói khác đi. */
const getErrorEmptyAnswer = () => (i18n.t("AI_NO_ANSWER_REPHRASE"));

const messageOfError = (error: any): string => {
  if (error instanceof JavisAiError) {
    return getErrorText()[error.code] ?? error.message ?? getErrorFallback();
  }
  return getErrorFallback();
};

/**
 * Lỗi **cấu hình**, gõ lại bao nhiêu lần cũng vẫn thế: thiếu biến môi trường, khoá xác thực sai /
 * hết hạn, assistant chưa publish. Khác lỗi mạng chốc lát — gặp loại này thì phải chặn ô nhập, để
 * người dùng không gõ thêm 3-4 câu rồi nhận đúng một thông báo lặp lại.
 */
const isPermanentError = (error: any): boolean =>
  error instanceof JavisAiError &&
  (error.code.startsWith("MISSING_AI_") ||
    error.code.startsWith("ERR-APIKEY-") ||
    error.code.startsWith("ERR-ASSISTANT-"));

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** "Hôm nay, 15:27" — nhãn thời điểm mở hội thoại, theo design. */
const todayLabel = (at: Date) =>
  i18n.t("TPL_TODAY_AT", { p0: String(at.getHours()).padStart(2, "0"), p1: String(
    at.getMinutes(),
  ).padStart(2, "0") });

/**
 * Chatbot **Javis AI** — nút mở ở thanh header + khung chat nổi góc dưới bên phải
 * (design 14/08/2026), nói chuyện với JAVIS AI qua **API tích hợp đối tác v1**
 * (`document/partner-chat-integration-guide.md`).
 *
 * <p>Một lượt hỏi đáp đi 2 chặng: `POST /v1/chat` chỉ ghi nhận câu hỏi và trả `streamUrl`, câu trả
 * lời chảy về qua SSE ở chặng sau. Component ghép các sự kiện `chunk` vào tin nhắn cuối của bot.</p>
 *
 * <p>Đóng khung chat chỉ huỷ luồng SSE đang chạy, **không** xoá hội thoại: `conversationId` và
 * danh sách tin nhắn được cất vào `sessionStorage` (xem `chatSessionStore`), mở lại là chat tiếp
 * đúng chỗ đang dở — cả lịch sử hiển thị lẫn ngữ cảnh AI đang nhớ. Chỉ khi hội thoại hết hạn phía
 * server (hiện là 1 giờ) mới bắt đầu lại từ đầu.</p>
 */
const JavisAiChat: FC = () => {
  const { t } = useTranslation();

  const { user } = useAuth();
  const [opened, { open, close }] = useDisclosure(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  /** Đang chờ mẩu chữ ĐẦU TIÊN của bot — lúc này hiện bong bóng ba chấm. */
  const [isWaiting, setIsWaiting] = useState(false);
  /** Gặp lỗi cấu hình → khoá ô nhập, gõ thêm cũng chỉ nhận lại đúng thông báo đó. */
  const [unavailable, setUnavailable] = useState(false);
  const [startedAt, setStartedAt] = useState<Date>(() => new Date());

  const viewport = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const streamingRef = useRef(false);
  const conversationRef = useRef<string>("");
  /** Mốc hết hạn của hội thoại hiện tại (ISO), để cất kèm vào sessionStorage. */
  const expiresAtRef = useRef<string>("");

  const userId = user?.userId ?? "";

  /**
   * Cất phiên chat lại sau mỗi thay đổi để đóng khung chat lúc nào cũng không mất gì.
   *
   * Ghi theo `messages` chứ không ghi trong `handleClose`: lúc đóng, luồng SSE có thể còn đang chảy
   * và `setMessages` cuối cùng chưa kịp áp dụng — ghi ở đó sẽ thiếu mất đoạn chữ vừa nhận.
   */
  useEffect(() => {
    if (!conversationRef.current || !messages.length) return;
    saveSession({
      conversationId: conversationRef.current,
      expiresAt: expiresAtRef.current,
      startedAt: startedAt.toISOString(),
      userId,
      messages,
    });
  }, [messages, startedAt, userId]);

  const scrollToBottom = useCallback(() => {
    viewport.current?.scrollTo({
      top: viewport.current.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (opened) scrollToBottom();
  }, [messages, isWaiting, opened, scrollToBottom]);

  const pushBot = (content: string) =>
    setMessages((prev) => [...prev, { id: newId(), role: "bot", content }]);

  /**
   * Nối một sự kiện SSE vào tin nhắn `botId`.
   *
   * @returns `true` khi lượt trả lời đã kết thúc (`done` hoặc `error`)
   */
  const applyEvent = (botId: string, { event, data }: JavisAiEvent): boolean => {
    const appendText = (text: string) => {
      if (!text) return;
      setIsWaiting(false);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botId ? { ...msg, content: msg.content + text } : msg,
        ),
      );
    };

    switch (event) {
      case "chunk":
        appendText(data?.content ?? "");
        return false;

      // Assistant hỏi lại để làm rõ — tài liệu §11 yêu cầu hiển thị như một chunk.
      case "clarification":
        appendText(data?.content ?? data?.message ?? data?.question ?? "");
        return false;

      /**
       * Model tự thu hồi phần chữ vừa đẩy. Tài liệu không mô tả cấu trúc `data`, nên xử lý phòng
       * thủ theo 2 dạng hay gặp: có `text` thì cắt đúng đoạn đuôi đó, có số lượng thì cắt bấy nhiêu
       * ký tự. Không khớp dạng nào thì bỏ qua — thà để thừa chữ còn hơn xoá nhầm câu trả lời.
       */
      case "retract_chunks": {
        const text = typeof data?.text === "string" ? data.text : "";
        const count = Number(data?.count ?? data?.chars ?? data?.length ?? 0);
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== botId) return msg;
            if (text && msg.content.endsWith(text)) {
              return { ...msg, content: msg.content.slice(0, -text.length) };
            }
            if (count > 0) {
              return { ...msg, content: msg.content.slice(0, -count) };
            }
            return msg;
          }),
        );
        return false;
      }

      case "error":
        appendText(data?.message || getErrorFallback());
        return true;

      case "done":
        return true;

      // start / citations / reasoning / tool_calls / ner_debug... — chưa hiển thị.
      default:
        return false;
    }
  };

  /** Mở một luồng SSE và bơm vào tin nhắn `botId` cho tới khi `done`. */
  const consumeStream = async (path: string, botId: string, signal: AbortSignal) => {
    let finished = false;
    await openEventStream({
      path,
      signal,
      onEvent: (evt) => {
        if (finished) return;
        finished = applyEvent(botId, evt);
      },
    });
  };

  /**
   * Tạo hội thoại mới.
   *
   * `userInfo.customerId` là **bắt buộc** (§5) và phải theo TỪNG NGƯỜI DÙNG chứ không phải theo
   * doanh nghiệp: JAVIS AI dùng nó để tách bộ nhớ hội thoại, lấy mã doanh nghiệp thì mọi nhân sự
   * của cùng công ty sẽ dùng chung ngữ cảnh của nhau.
   */
  const ensureConversation = async (signal: AbortSignal): Promise<string> => {
    const created = await createConversation({
      title: "Javis BIZ",
      userInfo: {
        customerId: user?.userId ?? "",
        custNo: user?.customerNo ?? "",
        name: user?.fullName ?? "",
        businessName: user?.businessName ?? "",
        channel: "BIZ_WEB",
      },
      signal,
    });
    conversationRef.current = created.id;
    expiresAtRef.current = expiresAtOf(created.expiresAt);
    return created.id;
  };

  const handleOpen = async () => {
    setInput("");
    setUnavailable(false);
    open();

    // Mở lại khung chat trong lúc hội thoại còn hạn → dựng lại đúng chỗ đang dở, KHÔNG tạo hội
    // thoại mới và KHÔNG chào lại. Chào lại sẽ vừa thừa vừa sai: AI phía server vẫn nhớ mạch cũ.
    const saved = loadSession(userId);
    if (saved) {
      conversationRef.current = saved.conversationId;
      expiresAtRef.current = saved.expiresAt;
      setStartedAt(new Date(saved.startedAt));
      setMessages(saved.messages);
      return;
    }

    setStartedAt(new Date());
    setMessages([]);
    conversationRef.current = "";
    expiresAtRef.current = "";

    if (!isAiConfigured()) {
      // Nói rõ thiếu cái nào — hai biến này do hai bên khác nhau cung cấp (hạ tầng vs team JAVIS AI).
      pushBot(
        getErrorText()[
          getAiBaseUrl() ? "MISSING_AI_ASSISTANT_ID" : "MISSING_AI_CHAT_HOST"
        ],
      );
      setUnavailable(true);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setIsWaiting(true);

    const botId = newId();
    setMessages([{ id: botId, role: "bot", content: "" }]);

    try {
      const conversationId = await ensureConversation(controller.signal);
      let finished = false;
      await streamGreeting({
        conversationId,
        signal: controller.signal,
        onEvent: (evt) => {
          if (!finished) finished = applyEvent(botId, evt);
        },
      });
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      // Lời chào hỏng KHÔNG chặn cuộc hội thoại: chỉ khi chưa tạo được conversation mới báo lỗi.
      const failed = !conversationRef.current;
      if (failed) setUnavailable(true);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botId && !msg.content
            ? {
                ...msg,
                content: failed ? messageOfError(error) : getGreetingFallback(),
              }
            : msg,
        ),
      );
    } finally {
      setIsWaiting(false);
      abortRef.current = null;
      // Assistant không cấu hình lời chào → stream kết thúc mà không có chunk nào.
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botId && !msg.content
            ? { ...msg, content: getGreetingFallback() }
            : msg,
        ),
      );
    }
  };

  /**
   * Đóng khung chat — chỉ dừng luồng SSE đang chảy, GIỮ NGUYÊN hội thoại.
   *
   * Không xoá `messages` / `conversationRef`: phiên đã được cất ở effect theo `messages`, và giữ
   * state trong bộ nhớ giúp mở lại tức thì, không phải đọc sessionStorage.
   */
  const handleClose = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    streamingRef.current = false;
    setIsWaiting(false);
    setInput("");
    close();
  };

  // Rời màn hình khi luồng còn chạy → huỷ, tránh setState trên component đã unmount.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const handleSend = async () => {
    const content = input.trim();
    if (!content || streamingRef.current) return;

    const botId = newId();
    setMessages((prev) => [
      ...prev,
      { id: newId(), role: "user", content },
      { id: botId, role: "bot", content: "" },
    ]);
    setInput("");
    setIsWaiting(true);
    streamingRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    /** Gửi câu hỏi rồi đọc phản hồi. Tách ra để chạy lại được sau khi tạo hội thoại mới. */
    const ask = async () => {
      const conversationId =
        conversationRef.current || (await ensureConversation(controller.signal));
      const streamUrl = await sendMessage({
        conversationId,
        message: content,
        signal: controller.signal,
      });
      await consumeStream(streamUrl, botId, controller.signal);
    };

    try {
      try {
        await ask();
      } catch (error: any) {
        // Hội thoại hết hạn (§7) — tạo cái mới rồi hỏi lại đúng một lần, người dùng không phải gõ lại.
        if (
          error instanceof JavisAiError &&
          CONVERSATION_GONE_CODES.includes(error.code)
        ) {
          conversationRef.current = "";
          expiresAtRef.current = "";
          clearSession();
          // Nói ra việc AI mất trí nhớ. Không báo thì người dùng thấy lịch sử vẫn còn trên màn
          // hình nhưng AI hỏi lại từ đầu — trông như lỗi, trong khi đây là hết hạn phía server.
          setMessages((prev) => {
            const at = prev.findIndex((msg) => msg.id === botId) - 1;
            const divider: ChatMessage = {
              id: newId(),
              role: "bot",
              content: getExpiredNotice(),
            };
            const next = [...prev];
            next.splice(Math.max(at, 0), 0, divider);
            return next;
          });
          await ask();
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      // Người dùng tự đóng chat — không phải lỗi, không hiện gì thêm.
      if (error?.name !== "AbortError") {
        if (isPermanentError(error)) setUnavailable(true);
        const text = messageOfError(error);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botId && !msg.content ? { ...msg, content: text } : msg,
          ),
        );
      }
    } finally {
      streamingRef.current = false;
      abortRef.current = null;
      setIsWaiting(false);
      // Luồng kết thúc mà không có chunk nào (assistant trả rỗng, hoặc `done` tới ngay). Bong bóng
      // rỗng KHÔNG được render nên người dùng chỉ thấy câu mình hỏi rồi im lặng — phải nói ra.
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botId && !msg.content
            ? { ...msg, content: getErrorEmptyAnswer() }
            : msg,
        ),
      );
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter gửi, Shift+Enter xuống dòng — thói quen chung của mọi khung chat.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const configured = isAiConfigured();

  return (
    <>
      {/* Nút mở ở thanh header — design: pill xanh, icon robot trắng */}
      <UnstyledButton
        onClick={opened ? handleClose : handleOpen}
        aria-label={t("CHAT_WITH_JAVIS_AI")}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 36,
          padding: "0 16px",
          borderRadius: 999,
          backgroundColor: "#2A81D0",
          color: "#FDFDFD",
          fontSize: 14,
          fontWeight: 600,
          whiteSpace: "nowrap",
          transition: "background-color .2s ease",
          "&:hover": { backgroundColor: "#1F6DB4" },
        }}
      >
        <BotAvatar size={20} variant="white" />
        {t("CHAT_WITH_JAVIS_AI")}
      </UnstyledButton>

      {opened && (
        <Portal>
          <Box
            className="javis-ai-panel"
            sx={{
              position: "fixed",
              right: 24,
              bottom: 24,
              zIndex: 300,
              width: 380,
              height: 560,
              maxHeight: "calc(100vh - 120px)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 12,
              border: "2px solid #2A81D0",
              backgroundColor: "#EAF4FF",
              boxShadow: "0 12px 32px rgba(16, 40, 80, .18)",
              // Màn hẹp: bám hai mép, không tràn ra ngoài viewport.
              "@media (max-width: 480px)": {
                right: 12,
                left: 12,
                width: "auto",
              },
            }}
          >
            {/* Thanh tiêu đề */}
            <Flex
              align="center"
              justify="space-between"
              px={16}
              py={12}
              sx={{
                background: "linear-gradient(90deg, #0B62C4 0%, #3AA9F0 100%)",
                flexShrink: 0,
              }}
            >
              <Text fz={20} fw={700} c="#FFFFFF">
                Javis AI
              </Text>
              <ActionIcon
                variant="transparent"
                onClick={handleClose}
                aria-label={t("CLOSE_CHAT_PANEL")}
                sx={{
                  color: "#FFFFFF",
                  backgroundColor: "rgba(255,255,255,.25)",
                  borderRadius: "50%",
                  "&:hover": { backgroundColor: "rgba(255,255,255,.35)" },
                }}
              >
                <IconX size={16} />
              </ActionIcon>
            </Flex>

            {/* Khung hội thoại */}
            <ScrollArea
              viewportRef={viewport}
              className="javis-ai-scroll"
              sx={{
                flex: 1,
                background:
                  "linear-gradient(180deg, #DCEBFB 0%, #EFF7FF 55%, #FFFFFF 100%)",
              }}
              px={14}
            >
              <Text fz={12} c="#8A94A6" ta="center" my={12}>
                {todayLabel(startedAt)}
              </Text>

              {messages.map((msg) => (
                <Fragment key={msg.id}>
                  {msg.role === "bot"
                    ? Boolean(msg.content) && (
                        <Flex align="flex-end" gap={8} mb={12}>
                          <Box sx={{ flexShrink: 0 }}>
                            <BotAvatar size={28} />
                          </Box>
                          <Box
                            className="javis-ai-markdown"
                            sx={{
                              maxWidth: "78%",
                              padding: "10px 14px",
                              borderRadius: 12,
                              backgroundColor: "#FFFFFF",
                              color: "#1A1A1A",
                              fontSize: 14,
                              lineHeight: 1.45,
                              wordBreak: "break-word",
                              boxShadow: "0 1px 2px rgba(16,40,80,.08)",
                            }}
                          >
                            <Markdown>{msg.content}</Markdown>
                          </Box>
                        </Flex>
                      )
                    : (
                      <Flex justify="flex-end" mb={12}>
                        <Box
                          sx={{
                            maxWidth: "78%",
                            padding: "10px 14px",
                            borderRadius: 12,
                            backgroundColor: "#C7D8F5",
                            color: "#1A1A1A",
                            fontSize: 14,
                            lineHeight: 1.45,
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {msg.content}
                        </Box>
                      </Flex>
                    )}
                </Fragment>
              ))}

              {isWaiting && (
                <Flex align="flex-end" gap={8} mb={12}>
                  <Box sx={{ flexShrink: 0 }}>
                    <BotAvatar size={28} />
                  </Box>
                  <Box
                    sx={{
                      padding: "12px 16px",
                      borderRadius: 12,
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 1px 2px rgba(16,40,80,.08)",
                    }}
                  >
                    <span className="javis-ai-typing">
                      <span />
                      <span />
                      <span />
                    </span>
                  </Box>
                </Flex>
              )}
            </ScrollArea>

            {/* Ô nhập */}
            <Flex
              align="center"
              gap={10}
              px={14}
              py={12}
              sx={{ backgroundColor: "#FFFFFF", flexShrink: 0 }}
            >
              <Textarea
                autosize
                minRows={1}
                maxRows={4}
                value={input}
                onChange={(e) => setInput(e.currentTarget.value)}
                onKeyDown={handleKeyDown}
                disabled={!configured || unavailable}
                placeholder={
                  configured && !unavailable
                    ? t("TYPE_A_MESSAGE")
                    : t("AI_NOT_AVAILABLE")
                }
                sx={{ flex: 1 }}
                styles={{
                  input: {
                    borderRadius: 10,
                    border: "1px solid #2A81D0",
                    fontSize: 14,
                    "&:focus": { borderColor: "#2A81D0" },
                  },
                }}
              />
              <ActionIcon
                size={40}
                radius="xl"
                onClick={handleSend}
                disabled={
                  !configured ||
                  unavailable ||
                  !input.trim() ||
                  streamingRef.current
                }
                aria-label={t("SEND")}
                sx={{
                  flexShrink: 0,
                  backgroundColor: "#2A81D0",
                  color: "#FFFFFF",
                  "&:hover": { backgroundColor: "#1F6DB4" },
                  "&[data-disabled]": {
                    backgroundColor: "#A9C8E8",
                    color: "#FFFFFF",
                    borderColor: "transparent",
                  },
                }}
              >
                <IconSend size={18} />
              </ActionIcon>
            </Flex>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default JavisAiChat;
